import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Award, BookOpen, Mail, Mic } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Reveal, Eyebrow } from "@/lib/motion";
import { TEAM } from "@/data/content";

export function TeamGrid({ openId = null }) {
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (openId) {
      const member = TEAM.find((m) => m.id === openId);
      if (member) setActive(member);
    }
  }, [openId]);
  return (
    <>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {TEAM.map((member, i) => (
          <Reveal key={member.id} delay={i * 0.1}>
            <Link
              to={`/about/${member.slug}`}
              data-testid={member.testId}
              className="group relative block w-full text-left"
            >
              <div className="relative aspect-square overflow-hidden border border-border" style={{ background: "radial-gradient(ellipse 80% 60% at 50% 32%, hsl(var(--gold) / 0.18), transparent 65%), linear-gradient(to bottom, hsl(var(--secondary)), hsl(var(--card)))" }}>
                <img
                  src={member.image}
                  alt={member.name}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-transparent" />
                <span className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center border border-gold/50 bg-background/50 text-gold opacity-0 backdrop-blur-md transition-opacity duration-300 group-hover:opacity-100">
                  <ArrowUpRight className="h-4 w-4" />
                </span>
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">{member.role}</p>
                  <h3 className="mt-1 font-serif text-xl font-medium text-white">{member.name}</h3>
                </div>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent data-lenis-prevent className="max-h-[85vh] max-w-2xl overflow-y-auto border-border bg-card p-8 sm:p-10">
          {active && (
            <>
              <DialogHeader>
                <Eyebrow>{active.role}</Eyebrow>
                <DialogTitle className="font-serif text-3xl font-medium tracking-tight">{active.name}</DialogTitle>
                <DialogDescription className="text-sm leading-relaxed text-muted-foreground">
                  {active.short}
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4 space-y-4">
                {active.bioSections
                  ? active.bioSections.map((s, i) => (
                      <div key={i} className="space-y-4">
                        {s.heading && (
                          <p className="pt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{s.heading}</p>
                        )}
                        {s.paragraphs.map((p, j) => (
                          <p key={j} className="text-sm leading-relaxed text-foreground/85">{p}</p>
                        ))}
                      </div>
                    ))
                  : active.bio.map((p, i) => (
                      <p key={i} className="text-sm leading-relaxed text-foreground/85">{p}</p>
                    ))}
              </div>
              <div className="mt-6 border-t border-border pt-6">
                <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-gold">
                  <Award className="h-3.5 w-3.5" /> Credentials
                </p>
                <ul className="mt-4 space-y-2">
                  {active.credentials.map((c) => (
                    <li key={c} className="flex items-start gap-3 text-sm text-foreground/80">
                      <span className="mt-2 h-1 w-1 shrink-0 bg-gold" />
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
              {active.publications && (
                <div className="mt-6 border-t border-border pt-6">
                  <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-gold">
                    <BookOpen className="h-3.5 w-3.5" /> Selected Publications
                  </p>
                  <ul className="mt-4 space-y-4">
                    {active.publications.map((p) => (
                      <li key={p.title}>
                        <p className="text-sm font-medium leading-snug text-foreground/90">{p.title}</p>
                        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                          {p.outlet} — {p.year}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {active.speaking && (
                <div className="mt-6 border-t border-border pt-6">
                  <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-gold">
                    <Mic className="h-3.5 w-3.5" /> Speaking Engagements
                  </p>
                  <ul className="mt-4 space-y-4">
                    {active.speaking.map((s) => (
                      <li key={s.title}>
                        <p className="text-sm font-medium leading-snug text-foreground/90">{s.title}</p>
                        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                          {s.org} — {s.year}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {active.email && (
                <a
                  href={`mailto:${active.email}`}
                  className="mt-6 inline-flex items-center gap-2 border border-gold/60 px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] text-gold transition-all duration-300 hover:bg-gold hover:text-primary-foreground"
                >
                  <Mail className="h-3.5 w-3.5" /> E-Mail {active.name.split(" ")[0]}
                </a>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
